import { centreOf } from 'app/pages/Teacher/Attendance/Attendance'

import {
  SECTIONS,
  addDays,
  isLeader,
  isPreschool,
  punctuality,
  scoresForDates,
  weekDays
} from './scoring'

// Actions tab (Satya, 27 Oct: option 1 of the mockup, rules only). Every
// rule that turns the Teachers tab's numbers into a to-do for the CD lives
// here, worked out over the last 4 full weeks before this Monday:
//
//   Urgent    = no classroom observation of anyone at a centre; or a
//               teacher's Weekly Submissions or Lesson Planner is 0 (nothing
//               handed in for 4 weeks).
//   This week = a section under 3 (the red band on the Teachers tab).
//   Fix data  = no Alfred account linked, or a preschool teacher on no class
//               in Alfred, so most sections can't be scored.
//
// Timeline (Satya, 27 Oct, "do 2 weeks"): the week's list is made once, on
// the first visit of the week, and saved, so it stays the same until Sunday.
// An action not ticked carries over each Monday ("Open since"). A ticked one
// is frozen for FREEZE_WEEKS full weeks after its tick week (Checking), then
// judged once over those weeks and kept: up 0.5 or more = Improved, down 0.5
// or more = Worse, else No change. If its rule still holds, the next Monday
// raises it again as round 2 (the server decides that, see
// models/scorecard_action.py).

// Actions whose Open shows just that section of the teacher's pop-up.
export const SECTION_KINDS = ['pun', 'mom', 'sub', 'plan']

export const WEAK = 3
export const MOVE = 0.5
export const FREEZE_WEEKS = 2

export const TAGS = { urgent: 'Urgent', week: 'This week', data: 'Fix data' }
export const ORDER = { urgent: 0, week: 1, data: 2 }

const f1 = (v) => v.toFixed(1)
const label = (key) => (SECTIONS.find((s) => s.key === key) || {}).label

export const METRIC_LABEL = {
  ...Object.fromEntries(SECTIONS.map((s) => [s.key, s.label])),
  seen: 'Classes observed',
  has: 'Has scores'
}

// One teacher's hand-ins (Weekly Submissions 'sub' / Lesson Planner 'plan').
const HAND_IN = {
  sub: {
    none: 'no weekly evaluations handed in for 4 weeks.',
    some: 'under 3 of 5 weekly evaluations handed in.',
    todo: 'Sit with her this week; get this week’s evaluations in by Friday.'
  },
  plan: {
    none: 'no lesson plans handed in for 4 weeks.',
    some: 'under 3 of 5 lesson plans handed in.',
    todo: 'Go through the Lesson Planner with her; get next week’s plan in by Friday.'
  }
}

const teacherActions = (t, s, dates) => {
  const out = []
  const base = { no: t.no, who: t.name, centre: centreOf(t) }
  const add = (tier, kind, metric, value, fact, todo) =>
    out.push({
      ...base,
      id: `${kind}|${t.no}`,
      tier,
      kind,
      metric,
      value,
      fact,
      todo
    })

  if (s.pun !== null && s.pun < WEAK) {
    const p = punctuality(t, dates)
    add(
      'week',
      'pun',
      'pun',
      s.pun,
      `Punctuality ${f1(s.pun)}: on time ${p.on} of ${p.worked} working days.`,
      'Talk about arrival time; check again next week.'
    )
  }
  // CD · DCD · AM are scored on punctuality only so far.
  if (isLeader(t)) return out

  if (!t.linked) {
    add(
      'data',
      'link',
      'has',
      0,
      'her Keka name matches no Alfred teacher, so Moments and hand-ins can’t be scored.',
      'Ask HQ to link her Alfred account.'
    )
    return out
  }
  if (isPreschool(t) && s.mom === null && s.sub === null && s.plan === null) {
    add(
      'data',
      'class',
      'has',
      0,
      'on no preschool class in Alfred in these weeks, so she can’t be scored.',
      'Ask HQ to add her to her class in Alfred.'
    )
    return out
  }

  ;['sub', 'plan'].forEach((key) => {
    const v = s[key]
    if (v === null || v >= WEAK) return
    const h = HAND_IN[key]
    add(
      v === 0 ? 'urgent' : 'week',
      key,
      key,
      v,
      `${label(key)} ${f1(v)}: ${v === 0 ? h.none : h.some}`,
      h.todo
    )
  })
  if (s.mom !== null && s.mom < WEAK)
    add(
      'week',
      'mom',
      'mom',
      s.mom,
      isPreschool(t)
        ? `Moments ${f1(s.mom)}: many Daily Updates late or missing.`
        : `Moments ${f1(s.mom)}: many children got under 2 moments a month.`,
      isPreschool(t)
        ? 'Check which update slips (arrival? lunch? nap?) and set a reminder for that time.'
        : 'Ask for at least 2 moments a month for every child in each class.'
    )
  return out
}

const observedIn = (t, first, last) =>
  (t.observations || []).filter((o) => o.d >= first && o.d <= last).length

// Everything to do for these teachers, most urgent first (whole-centre ones
// first within a tier, then lowest number), and who is doing
// well (overall 4 or more, best first, up to 3).
export const raiseActions = (teachers, dates) => {
  if (!dates.length) return { list: [], good: [] }
  const first = dates[0]
  const last = dates[dates.length - 1]
  const scored = teachers.map((t) => ({ t, s: scoresForDates(t, dates) }))
  const list = scored.flatMap(({ t, s }) => teacherActions(t, s, dates))

  // A centre where none of its preschool teachers was observed: one action
  // for the whole centre, naming the lowest overall teachers to start with.
  const byCentre = {}
  scored
    .filter(({ t }) => isPreschool(t) && !isLeader(t))
    .forEach((x) => {
      const c = centreOf(x.t)
      byCentre[c] = byCentre[c] || []
      byCentre[c].push(x)
    })
  Object.entries(byCentre).forEach(([centre, xs]) => {
    if (xs.some(({ t }) => observedIn(t, first, last))) return
    const lowest = xs
      .filter(({ s }) => s.overall !== null)
      .sort((a, b) => a.s.overall - b.s.overall)
      .slice(0, 2)
      .map(({ t }) => t.name)
    list.push({
      id: `obs|${centre}`,
      tier: 'urgent',
      kind: 'obs',
      metric: 'seen',
      value: 0,
      no: null,
      who: `Whole centre · ${centre}`,
      centre,
      fact: `no classroom observation of any of the ${xs.length} teachers in 4 weeks, so Environment and Facilitation are blank.`,
      todo: lowest.length
        ? `Observe 3 classes this week, starting with ${lowest.join(
            ' and '
          )} (lowest overall).`
        : 'Observe 3 classes this week.'
    })
  })

  list.forEach((a) => {
    a.tag = TAGS[a.tier]
  })
  list.sort(
    (a, b) =>
      ORDER[a.tier] - ORDER[b.tier] ||
      (a.no ? 1 : 0) - (b.no ? 1 : 0) ||
      (a.value ?? 0) - (b.value ?? 0) ||
      a.who.localeCompare(b.who)
  )
  const good = scored
    .filter(({ s }) => s.overall !== null && s.overall >= 4)
    .sort((a, b) => b.s.overall - a.s.overall)
    .slice(0, 3)
    .map(({ t, s }) => ({ no: t.no, who: t.name, overall: s.overall }))
  return { list, good }
}

// How the saved list is shown: For HQ = data fixes and the actions of
// CD · DCD · AM themselves; To do = all the rest (Satya, 28 Oct pm: "show
// all", no longer one per teacher or a top 7).
export const splitForCd = (rows, teachers) => {
  const leaders = new Set(teachers.filter(isLeader).map((t) => t.no))
  const hq = []
  const todo = []
  rows.forEach((a) =>
    (a.tier === 'data' || leaders.has(a.employee_no) ? hq : todo).push(a)
  )
  return { todo, hq }
}

// A saved tick and the action it belongs to share this key.
export const actionKey = (a) => `${a.kind}|${a.no || a.employee_no || a.centre}`

// Monday of the week a day falls in.
export const mondayOf = (iso) => {
  const day = new Date(`${iso}T00:00:00`).getDay()
  return addDays(iso, -((day + 6) % 7))
}

// The number a saved action is judged on, over the given days.
const valueOver = (a, teachers, dates) => {
  if (a.metric === 'seen') {
    const first = dates[0]
    const last = dates[dates.length - 1]
    return teachers
      .filter((t) => centreOf(t) === a.centre && !isLeader(t))
      .reduce((n, t) => n + observedIn(t, first, last), 0)
  }
  const t = teachers.find((x) => x.no === a.employee_no)
  if (!t) return null
  const s = scoresForDates(t, dates)
  if (a.metric === 'has')
    return s.mom !== null || s.sub !== null || s.plan !== null ? 1 : 0
  return s[a.metric]
}

// The days the week's list is made from: the 4 full weeks before this
// Monday, so a visit later in the week works it out the same way.
export const listDates = (weeks, today) =>
  weeks
    .filter((m) => m < mondayOf(today))
    .slice(-4)
    .flatMap(weekDays)

// Monday a ticked action gets its result: after FREEZE_WEEKS full weeks.
export const resultDue = (a) => addDays(a.done_week, 7 * (FREEZE_WEEKS + 1))

// result: the saved one once judged; before that 'Checking' with the number
// so far, or — once the freeze is over — the result to save (final: false).
export const judge = (a, teachers, weeks, today) => {
  if (a.result)
    return { result: a.result, now: a.result_value, weeks: FREEZE_WEEKS }
  const after = weeks
    .filter((m) => m > a.done_week && addDays(m, 6) < today)
    .slice(0, FREEZE_WEEKS)
  const now = after.length
    ? valueOver(a, teachers, after.flatMap(weekDays))
    : null
  if (after.length < FREEZE_WEEKS || now === null)
    return { result: 'Checking', now, weeks: after.length, due: resultDue(a) }
  let result = 'No change'
  // Counts that only need to be above 0: classes observed, has scores.
  if (a.metric === 'seen' || a.metric === 'has') {
    if (now > 0) result = 'Improved'
  } else {
    const diff = now - (a.value_at ?? 0)
    if (diff >= MOVE) result = 'Improved'
    else if (diff <= -MOVE) result = 'Worse'
  }
  return { result, now, weeks: after.length, toSave: true }
}

// Ticked actions whose freeze is over and that have no saved result yet.
export const resultsToSave = (actions, teachers, weeks, today) =>
  actions
    .filter((a) => a.done_at && !a.result)
    .map((a) => ({ a, j: judge(a, teachers, weeks, today) }))
    .filter(({ j }) => j.toSave)
    .map(({ a, j }) => ({ id: a.id, result: j.result, result_value: j.now }))

// History (Satya, 28 Oct): months later, did the teacher reach the target?
// Target = out of red (WEAK, 3.0) on the number the action is about; for
// observations and data fixes, at least one / fixed. Worked out from the
// number saved after the freeze (result_value), so old actions get it too.
export const HISTORY = {
  met: 'Target met',
  miss: 'Missed',
  checking: 'Checking',
  open: 'Open',
  notDone: 'Not done'
}

export const targetOf = (a) =>
  a.metric === 'seen' || a.metric === 'has' ? 1 : WEAK

export const historyStatus = (a) => {
  if (a.done_at) {
    if (!a.result || a.result_value === null || a.result_value === undefined)
      return HISTORY.checking
    return a.result_value >= targetOf(a) ? HISTORY.met : HISTORY.miss
  }
  return a.closed_week ? HISTORY.notDone : HISTORY.open
}

// Worst first, so a cell with several actions shows the one to look at.
const WORST = [
  HISTORY.miss,
  HISTORY.notDone,
  HISTORY.open,
  HISTORY.checking,
  HISTORY.met
]

// Grid: one row per teacher (or whole centre), one column per Monday; a
// cell = that teacher's actions raised that week. Always the last SPAN
// Mondays up to thisMonday (plus any older week with an action), and every
// teacher in `people`, so it reads as a grid from the first week on.
export const HISTORY_SPAN = 12

export const historyGrid = (actions, people = [], thisMonday = null) => {
  const span = thisMonday
    ? Array.from({ length: HISTORY_SPAN }, (_, i) =>
        addDays(thisMonday, -7 * (HISTORY_SPAN - 1 - i))
      )
    : []
  const weeks = [
    ...new Set([...span, ...actions.map((a) => a.week_start)])
  ].sort()
  const rows = {}
  people.forEach((t) => {
    rows[t.no] = { key: t.no, who: t.name, cells: {} }
  })
  actions.forEach((a) => {
    const key = a.employee_no || `centre|${a.centre}`
    const r = (rows[key] = rows[key] || { key, who: a.who, cells: {} })
    ;(r.cells[a.week_start] = r.cells[a.week_start] || []).push(a)
  })
  const list = Object.values(rows).map((r) => ({
    ...r,
    status: Object.fromEntries(
      Object.entries(r.cells).map(([w, xs]) => [
        w,
        WORST.find((s) => xs.some((a) => historyStatus(a) === s))
      ])
    )
  }))
  list.sort(
    (a, b) =>
      (a.key.startsWith('centre|') ? 0 : 1) -
        (b.key.startsWith('centre|') ? 0 : 1) || a.who.localeCompare(b.who)
  )
  return { weeks, rows: list }
}
